<template>
    <main class="sophae-page refined-home">
        <section class="sophae-home">
            <div class="hero-shell">
                <div class="hero-copy">
                    <p class="overline"><span class="status-light"></span> Portfolio intelligence for private equity</p>
                    <h1>Portfolio monitoring.<br /><span>Built around your thesis.</span></h1>
                    <p class="hero-intro">Sophae connects what is happening inside your portfolio companies to why you invested, helping company leaders and your investment team make better-informed decisions.</p>
                    <button class="primary-action" @click="onRequestDemo">Request a demo <span aria-hidden="true">↗</span></button>
                </div>
                <div class="hero-art" role="img" aria-label="A floating sculptural head with a luminous cyan brain">
                    <div class="art-halo"></div><div class="art-orbit"></div>
                    <div ref="threeContainer" class="three-layer"></div>
                    <div class="art-caption"><span class="tiny-cross">+</span> Institutional wisdom, compounded.</div>
                </div>
            </div>
            <div class="hero-baseline"><span>Compounding Decision Intelligence</span><a class="hero-scroll" href="#capabilities">What Sophae Does <span aria-hidden="true">↓</span></a></div>
        </section>
        <ProductCapabilities />
        <InvestmentPreview />
        <PortfolioCaseStudy />
        <PhilosophySection />
        <IndustryReading />
        <section class="editorial-section closing-section"><p class="overline">Start a conversation</p><h2>Bring one company.<br /><span>And the questions that matter.</span></h2><p>Explore Sophae with the investment thesis your team is working to deliver.</p><button class="primary-action" @click="onRequestDemo">Request a demo <span aria-hidden="true">↗</span></button></section>
    </main>
</template>

<script>
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import InvestmentPreview from '@/components/InvestmentPreview.vue'
import PortfolioCaseStudy from '@/components/PortfolioCaseStudy.vue'
import ProductCapabilities from '@/components/ProductCapabilities.vue'
import IndustryReading from '@/components/IndustryReading.vue'
import PhilosophySection from '@/components/PhilosophySection.vue'

export default {
    name: 'SophaeHome',
    components: { InvestmentPreview, PortfolioCaseStudy, ProductCapabilities, IndustryReading, PhilosophySection },
    data() {
        return {
            three: {
                scene: null,
                camera: null,
                renderer: null,
                head: null,
                brain: null,
                brainMixer: null,
                clock: null,
            },
            mouse: { x: 0.5, y: 0.5 },
            scrollObserver: null,
            scrollAnimations: [],
            scrollMotionQuery: null,
            animationId: null,





        }
    },

    mounted() {
        this.animate = this.animate.bind(this)
        this.initThree()
        window.addEventListener('resize', this.onResize)
        window.addEventListener('mousemove', this.onMouseMove)
        this.initScrollReveals()
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        window.removeEventListener('mousemove', this.onMouseMove)
        this.clearScrollReveals()
        if (this.scrollMotionQuery) this.scrollMotionQuery.removeEventListener('change', this.onScrollMotionChange)
        if (this.animationId) cancelAnimationFrame(this.animationId)
        if (this.three.scene) this.three.scene.traverse(obj => {
            if (obj.geometry) obj.geometry.dispose()
            if (obj.material) {
                const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
                materials.forEach(material => material.dispose())
            }
        })
        if (this.three.renderer) this.three.renderer.dispose()
    },

    methods: {
        onRequestDemo() {
            this.$root.$emit('open-demo')
        },
        onJoinWaitlist() {
            this.$root.$emit('open-waitlist')
        },
        scrollTo(id) {
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        },

        getHeadPosition() { return { x: 0, y: -0.55, z: 0.5 } },
        getHeadScale() { return { x: 1.3, y: 1.3, z: 1.3 } },
        getBrainPosition() { return { x: 0, y: 0.55, z: 0.5 } },
        getBrainScale() { return { x: 5, y: 5, z: 5 } },

        initThree() {
            const container = this.$refs.threeContainer
            if (!container) return

            const width = container.clientWidth
            const height = container.clientHeight

            const scene = new THREE.Scene()
            scene.background = null

            const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 100)
            camera.position.z = 6

            const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
            renderer.setSize(width, height)
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
            container.appendChild(renderer.domElement)

            const ambient = new THREE.AmbientLight(0xb7c7ce, 0.55)
            const dir = new THREE.DirectionalLight(0xe4eef2, 1.5)
            dir.position.set(4, 7, 5)
            scene.add(ambient)
            scene.add(dir)
            const rim = new THREE.DirectionalLight(0xadc8d1, 1.35)
            rim.position.set(-3, 2, -2)
            scene.add(rim)

            const clock = new THREE.Clock()

            this.three.scene = scene
            this.three.camera = camera
            this.three.renderer = renderer
            this.three.clock = clock

            const headLoader = new GLTFLoader()
            headLoader.load(
                '/models/basic_head_structure_gltf/scene.gltf',
                (gltf) => {
                    const head = gltf.scene
                    head.rotation.y = Math.PI
                    const s = this.getHeadScale()
                    head.scale.set(s.x, s.y, s.z)
                    const p = this.getHeadPosition()
                    head.position.set(p.x, p.y, p.z)

                    head.traverse((child) => {
                        if (child.isMesh) {
                            // The source head duplicates vertices along its facets. Weld positions
                            // before recalculating normals to keep the silhouette but soften shading.
                            const sourceGeometry = child.geometry
                            const smoothGeometry = sourceGeometry.clone()
                            smoothGeometry.deleteAttribute('normal')
                            child.geometry = mergeVertices(smoothGeometry, 0.0001)
                            child.geometry.computeVertexNormals()
                            smoothGeometry.dispose()
                            sourceGeometry.dispose()
                            const mat = new THREE.MeshStandardMaterial({
                                color: 0x182026,
                                metalness: 0.32,
                                roughness: 0.62,
                                transparent: true,
                                opacity: 1,
                            })
                            mat.depthWrite = false

                            mat.onBeforeCompile = (shader) => {
                                shader.uniforms.uMouse = { value: new THREE.Vector2(0.5, 0.5) }
                                shader.uniforms.uRadius = { value: 0.02 }
                                shader.uniforms.uEdgeSoftness = { value: 0.20 }
                                shader.uniforms.uTime = { value: 0.0 }
                                shader.uniforms.uResolution = {
                                    value: new THREE.Vector2(
                                        renderer.domElement.width,
                                        renderer.domElement.height
                                    ),
                                }

                                shader.fragmentShader =
                                    `
                  uniform vec2 uMouse;
                  uniform float uRadius;
                  uniform float uEdgeSoftness;
                  uniform float uTime;
                  uniform vec2 uResolution;

                  float hash(vec2 p) {
                    p = fract(p * vec2(123.34, 456.21));
                    p += dot(p, p + 34.345);
                    return fract(p.x * p.y);
                  }

                  float noise(vec2 p) {
                    vec2 i = floor(p);
                    vec2 f = fract(p);
                    float a = hash(i);
                    float b = hash(i + vec2(1.0, 0.0));
                    float c = hash(i + vec2(0.0, 1.0));
                    float d = hash(i + vec2(1.0, 1.0));
                    vec2 u = f * f * (3.0 - 2.0 * f);
                    return mix(a, b, u.x) +
                           (c - a) * u.y * (1.0 - u.x) +
                           (d - b) * u.x * u.y;
                  }
                  ` +
                                    shader.fragmentShader.replace(
                                        '#include <dithering_fragment>',
                                        `
                      vec2 screenUv = gl_FragCoord.xy / uResolution;
                      float distToMouse = distance(screenUv, uMouse);
                      float n = noise(screenUv * 4.0 + uTime * 0.7);
                      float radius = uRadius + (n - 0.5) * uEdgeSoftness * 0.9;
                      float mask = smoothstep(radius, radius + uEdgeSoftness, distToMouse);
                      gl_FragColor.a *= mask;
                      #include <dithering_fragment>
                    `
                                    )

                                mat.userData.shader = shader
                            }

                            child.material = mat
                            child.renderOrder = 3
                        }
                    })

                    scene.add(head)
                    this.three.head = head
                    this.loadBrain()
                }
            )

            this.animate()
        },

        loadBrain() {
            const scene = this.three.scene
            if (!scene) return

            const dracoLoader = new DRACOLoader()
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')

            const brainLoader = new GLTFLoader()
            brainLoader.setDRACOLoader(dracoLoader)

            brainLoader.load(
                '/models/hologram/brain_hologram_animated.glb',
                (gltf) => {
                    const brainRoot = gltf.scene
                    const rays = brainRoot.getObjectByName("holo_brain_rays")
                    if (rays) rays.visible = false

                    const p = this.getBrainPosition()
                    brainRoot.position.set(p.x, p.y, p.z)
                    const s = this.getBrainScale()
                    brainRoot.scale.set(s.x, s.y, s.z)

                    brainRoot.traverse((child) => {
                        if (child.isMesh) {
                            child.renderOrder = 2
                            child.material.transparent = true
                            child.material.opacity = 0.9
                            child.material.depthWrite = false
                            child.material.emissive = new THREE.Color(0x38bdf8)
                            child.material.emissiveIntensity = 0.85
                        }
                    })

                    scene.add(brainRoot)
                    this.three.brain = brainRoot
                },
                undefined,
                (err) => {
                    console.error('[BRAIN] error:', err)
                }
            )
        },

        animate() {
            const { scene, camera, renderer, head, brain, brainMixer, clock } = this.three
            if (!scene || !camera || !renderer || !clock) return

            const delta = clock.getDelta()
            const t = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : clock.elapsedTime

            if (brainMixer) brainMixer.update(delta)

            if (head) {
                const rotY = Math.sin(t * 0.22) * 0.065 + this.mouse.x * 0.12
                const rotX = Math.sin(t * 0.18) * 0.025 + this.mouse.y * 0.045
                head.rotation.y = rotY
                head.rotation.x = rotX

                head.traverse((child) => {
                    if (
                        child.isMesh &&
                        child.material &&
                        child.material.userData &&
                        child.material.userData.shader
                    ) {
                        const shader = child.material.userData.shader
                        if (!shader.uniforms || !shader.uniforms.uMouse) return
                        shader.uniforms.uMouse.value.set(this.mouse.x, 1.0 - this.mouse.y)
                        shader.uniforms.uTime.value = t
                        shader.uniforms.uResolution.value.set(
                            renderer.domElement.width,
                            renderer.domElement.height
                        )
                    }
                })

                if (brain) {
                    brain.quaternion.copy(head.quaternion)
                    brain.rotation.y += -0.11
                }
            }

            renderer.render(scene, camera)
            this.animationId = requestAnimationFrame(this.animate)
        },

        onResize() {
            const container = this.$refs.threeContainer
            const { camera, renderer } = this.three
            if (!container || !camera || !renderer) return
            const width = container.clientWidth
            const height = container.clientHeight
            camera.aspect = width / height
            camera.updateProjectionMatrix()
            renderer.setSize(width, height)
        },

        clearScrollReveals() {
            if (this.scrollObserver) this.scrollObserver.disconnect()
            this.scrollAnimations.forEach(animation => animation.cancel())
            this.scrollAnimations = []
        },
        onScrollMotionChange(event) {
            if (event.matches) this.clearScrollReveals()
        },
        initScrollReveals() {
            this.scrollMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
            this.scrollMotionQuery.addEventListener('change', this.onScrollMotionChange)
            if (this.scrollMotionQuery.matches || !window.IntersectionObserver) return
            // Content stays visible by default, including anchor navigation and keyboard focus.
            this.scrollObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    this.scrollObserver.unobserve(entry.target)
                    if (entry.target.contains(document.activeElement)) return
                    const animation = entry.target.animate([
                        { opacity: 0.35, transform: 'translateY(18px)' },
                        { opacity: 1, transform: 'translateY(0)' },
                    ], { duration: 650, easing: 'cubic-bezier(.22,1,.36,1)' })
                    this.scrollAnimations.push(animation)
                    animation.onfinish = () => {
                        this.scrollAnimations = this.scrollAnimations.filter(item => item !== animation)
                    }
                })
            }, { threshold: 0.08 })
            this.$el.querySelectorAll('.capability-heading, .capability-card, #perspective, #portfolio-example, #philosophy, #further-reading, .closing-section').forEach(element => {
                if (element.getBoundingClientRect().top >= window.innerHeight) this.scrollObserver.observe(element)
            })
        },
        onMouseMove(e) {
            const container = this.$refs.threeContainer
            if (!container) return
            const rect = container.getBoundingClientRect()
            const x = (e.clientX - rect.left) / rect.width
            const y = (e.clientY - rect.top) / rect.height
            this.mouse.x = THREE.MathUtils.clamp(x, 0, 1)
            this.mouse.y = THREE.MathUtils.clamp(y, 0, 1)
        },
    },
}
</script>

<style scoped>
.refined-home{background:#04060b;
color:#f2f1ed;
overflow:hidden;
font-family:Inter,Arial,sans-serif}
.sophae-home{position:relative;
padding:96px 0 0;
min-height:100svh;
box-sizing:border-box;
display:flex;
flex-direction:column;
background:radial-gradient(ellipse at 80% 40%,#0b192230,transparent 60%)}
.hero-shell,.editorial-section,.hero-baseline{max-width:1240px;
margin:auto;
width:calc(100% - 96px)}
.hero-shell{display:grid;
grid-template-columns:1fr 1fr;
align-items:center;
min-height:690px;
flex:1;
width:calc(100% - 96px)}
.hero-copy{z-index:2;
padding:55px 0}
.overline{font-size:10px;
letter-spacing:.19em;
text-transform:uppercase;
color:#a8aaa9;
margin:0 0 30px;
display:flex;
align-items:center;
gap:10px}
.status-light{display:inline-block;
width:5px;
height:5px;
background:#a8d0e8;
border-radius:50%;
box-shadow:0 0 12px #a8d0e866;
flex:none}
h1{font-size:clamp(48px,5.8vw,82px);
line-height:1.04;
letter-spacing:-.055em;
font-weight:400;
margin:0 0 28px}
h1>span{color:#aec8db}
h2{font-size:clamp(32px,3.6vw,50px);
font-weight:400;
line-height:1.15;
letter-spacing:-.04em;
margin:0}
h2>span{color:#8d939d}
.hero-intro{font-size:19px;
max-width:420px;
line-height:1.5;
margin:0 0 12px}
.hero-detail{font-size:14px;
line-height:1.8;
color:#939aa6;
max-width:390px;
margin-bottom:28px}
.primary-action{display:inline-flex;
align-items:center;
gap:40px;
padding:15px 21px;
background:#d2e3ee;
color:#101820;
font-size:13px;
border-radius:4px;
transition:background .2s,transform .2s}
.primary-action:hover{background:#e8f2f8;
transform:translateY(-2px)}
.quiet-link{display:block;
width:fit-content;
color:#a6aab2!important;
text-decoration:none;
font-size:12px;
margin-top:24px}
.quiet-link span{margin-left:22px}
.hero-art{height:630px;
position:relative;
isolation:isolate}
.three-layer{position:absolute;
inset:0;
z-index:2}
.art-halo{position:absolute;
inset:20% 0 10%;
background:radial-gradient(ellipse,#397e991b,transparent 66%);
filter:blur(20px)}
.art-orbit{position:absolute;
width:90%;
height:75%;
top:9%;
left:5%;
border:1px solid #84b3bf12;
border-radius:50%;
transform:rotate(-24deg)}
.art-caption{position:absolute;
bottom:0;
left:0;
right:0;
text-align:center;
font:10px monospace;
color:#6d7c8b;
letter-spacing:.06em}
.tiny-cross{color:#aec8db;
margin-right:15px}
.hero-baseline{display:flex;
justify-content:space-between;
border-top:1px solid #ffffff12;
padding:24px 0;
font-size:10px;
letter-spacing:.06em;
color:#717c87}
.editorial-section{padding:110px 0;
border-bottom:1px solid #ffffff12;
scroll-margin-top:80px}
.editorial-split{display:grid;
grid-template-columns:1.3fr 1fr;
gap:80px;
align-items:center}
.editorial-split p,.lens-copy p,.philosophy-lede,.closing-section>p:not(.overline){font-size:16px;
line-height:1.8;
color:#929aa6;
margin:0}
.principle-row{display:flex;
align-items:center;
gap:26px;
margin-top:65px;
color:#c3c6ca;
font-size:12px}
.principle-row i{height:1px;
flex:1;
background:linear-gradient(90deg,#aec8db55,#84c1da33)}
.section-heading{margin-bottom:48px}
.lens-layout{display:grid;
grid-template-columns: .9fr 1.2fr;
gap:85px}
.lens-tabs{display:flex;
flex-direction:column}
.lens-tabs button{display:flex;
align-items:center;
text-align:left;
gap:22px;
border-bottom:1px solid #ffffff12;
padding:20px 0;
color:#818995;
font-size:15px;
transition:color .2s}
.lens-tabs button span:first-child{font:10px monospace}
.lens-tabs button span:last-child{margin-left:auto;
opacity:0}
.lens-tabs button.selected{color:#d2e3ee}
.lens-tabs button.selected span:last-child{opacity:1}
.lens-copy{padding-top:32px;
min-height:190px}
.lens-copy h3{font-weight:400;
font-size:22px;
line-height:1.35;
margin-bottom:16px;
letter-spacing:-.02em}
.lens-copy p{font-size:14px}
.signal-panel{border:1px solid #ffffff15;
border-radius:10px;
background:radial-gradient(ellipse at 70% 70%,#17344238,transparent 70%),#080d14;
overflow:hidden}
.panel-meta{padding:22px;
border-bottom:1px solid #ffffff0c;
display:flex;
justify-content:space-between;
font:8px monospace;
letter-spacing:.06em;
color:#7c8896}
.panel-meta span:last-child{color:#a1b9ca}
.signal-flow{padding:35px 44px;
animation:arrive .6s ease}
.signal-node{border:1px solid #acc9dd30;
background:#acc9dd05;
padding:18px 22px;
border-radius:4px}
.signal-node small{display:block;
font:8px monospace;
letter-spacing:.1em;
color:#a5bbcb;
margin-bottom:9px}
.signal-node strong{font-size:16px;
font-weight:400}
.evidence-node{border-color:#85c5df30;
background:#85c5df05}
.evidence-node small{color:#89aec1}
.signal-track{height:40px;
width:1px;
background:#88b2c52b;
margin:auto;
position:relative;
overflow:hidden}
.signal-track span{position:absolute;
top:-15px;
width:1px;
height:15px;
background:#b5dce7;
box-shadow:0 0 9px #85c5df;
animation:signal 2.8s linear infinite}
.signal-question{font-size:13px;
display:flex;
align-items:center;
gap:12px;
padding:18px;
border:1px solid #ffffff10;
border-radius:4px}
.panel-foot{padding:18px;
text-align:center;
color:#70818e;
font:9px monospace;
border-top:1px solid #ffffff0b}
.philosophy-section{background:radial-gradient(ellipse at 50% 20%,#17232925,transparent 65%)}
.philosophy-lede{max-width:610px;
margin-top:28px}
.principles{display:grid;
grid-template-columns:repeat(3,1fr);
gap:55px;
margin-top:65px}
.principles article{border-top:1px solid #ffffff18;
padding-top:24px}
.principles article>span{font:10px monospace;
color:#96afc3}
.principles h3{font-size:20px;
font-weight:400;
margin:18px 0 12px}
.principles p{font-size:14px;
color:#8d97a3;
line-height:1.7;
max-width:270px}
.closing-section{text-align:center;
padding:120px 0 140px;
background:radial-gradient(ellipse at 50% 100%,#8fb9d710,transparent 65%)}
.closing-section .overline{justify-content:center}
.closing-section>p:not(.overline){margin:26px auto 30px;
max-width:480px}
.primary-action:focus-visible,.quiet-link:focus-visible,.lens-tabs button:focus-visible{outline:2px solid #9bcfe0;
outline-offset:6px}
@keyframes signal{to{top:100%}
}
@keyframes arrive{from{opacity:.4;
transform:translateY(6px)}
to{opacity:1;
transform:translateY(0)}
}
@media(max-width:960px){.hero-shell,.editorial-section,.hero-baseline{width:calc(100% - 48px)}
.hero-shell{min-height:600px;
grid-template-columns:1fr 1fr}
.hero-art{height:520px}
.hero-copy{padding:35px 0}
h1{font-size:52px}
.hero-intro{font-size:17px}
.editorial-split,.lens-layout{gap:35px}
.editorial-section{padding:80px 0}
.principles{gap:25px}
}
@media(max-width:650px){.sophae-home{padding-top:95px}
.hero-shell{display:flex;
flex-direction:column;
align-items:stretch}
.hero-copy{padding-bottom:0}
.hero-art{height:370px;
margin:5px -20px 20px}
.hero-baseline{font-size:9px;
gap:20px}
.hero-baseline span:last-child{text-align:right}
h1{font-size:44px}
.hero-detail{max-width:340px}
.editorial-split,.lens-layout{grid-template-columns:1fr;
gap:28px}
.principle-row{gap:12px;
font-size:9px;
margin-top:35px}
.principles{grid-template-columns:1fr;
gap:20px;
margin-top:40px}
.principles p{max-width:none}
.signal-flow{padding:25px}
.panel-meta{font-size:7px;
padding:15px}
.lens-copy{min-height:0;
padding-bottom:12px}
.editorial-section{padding:65px 0}
.art-caption{bottom:0}
.closing-section{padding-bottom:85px}
}
@media(prefers-reduced-motion:reduce){*,*::before,*::after{animation:none!important;
transition:none!important;
scroll-behavior:auto!important}
}


/* Atmospheric layers sit behind the content, leaving text contrast intact. */
.refined-home {
    position: relative;
    isolation: isolate;
    background: radial-gradient(ellipse at 85% 12%, #112a333d, transparent 36%),
        radial-gradient(ellipse at 0% 44%, #24313a26, transparent 35%), #04060b;
}
.refined-home::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    opacity: .24;
    background-image: radial-gradient(#b4c8cd65 .65px, transparent .8px);
    background-size: 31px 31px;
    mask-image: linear-gradient(transparent, #000 18%, transparent 46%, #000 70%, transparent);
}
.sophae-home::before {
    content: '';
    position: absolute;
    width: 85%;
    height: 65%;
    top: 14%;
    right: -24%;
    pointer-events: none;
    background: conic-gradient(from 215deg at 52% 48%, transparent 0deg, #7cbedb12 24deg, transparent 52deg, #a4c6e008 75deg, transparent 105deg);
    filter: blur(35px);
    animation: atmosphere 18s ease-in-out infinite alternate;
}
.hero-art::after {
    content: '';
    position: absolute;
    bottom: 8%;
    left: 13%;
    width: 74%;
    height: 12%;
    border-radius: 50%;
    background: radial-gradient(ellipse, #5a9ba32b, transparent 68%);
    filter: blur(18px);
    transform: rotate(-8deg);
    z-index: -1;
}
.art-halo {
    inset: 5% -15% 0;
    background: radial-gradient(ellipse at 50% 45%, #407a922a, #17394312 45%, transparent 70%);
    filter: blur(30px);
}
.art-orbit {
    border-color: #8abfc21b;
    box-shadow: 0 0 70px #72bfdb06, inset 0 0 60px #72bfdb04;
}
.signal-panel { box-shadow: 0 22px 75px #0006, 0 0 80px #569dad08; }
.closing-section { background: radial-gradient(ellipse at 50% 100%, #6586ab1c, transparent 65%); }
@keyframes atmosphere { from { transform: rotate(-5deg) scale(.97); opacity: .55; } to { transform: rotate(5deg) scale(1.06); opacity: 1; } }
@media(prefers-reduced-motion:reduce) { .sophae-home::before { animation: none; } }

/* Editorial finish: legible detail, measured spacing, quieter highlights. */
h1 { font-size: clamp(44px, 4.4vw, 64px); letter-spacing: -.038em; line-height: 1.09; font-weight: 400; }
h2 { letter-spacing: -.028em; line-height: 1.2; }
.hero-intro { font-size: 18px; line-height: 1.65; max-width: 440px; margin-bottom: 32px; color: #aab4bf; }
.hero-scroll { color: #b3c7d6!important; text-decoration:none; display:flex; align-items:center; gap:24px; }
.hero-scroll:hover { color:#fff!important; }
.hero-scroll:focus-visible { outline:2px solid #9bcfe0; outline-offset:6px; }
.hero-detail, .lens-copy p, .principles p { font-size: 15px; color: #a4acb6; }
.overline { font-size: 11px; letter-spacing: .15em; color: #b0b5bb; }
.hero-baseline { font-size: 11px; color: #929ba5; }
.art-caption { font: 11px Inter, Arial, sans-serif; color: #99a6af; letter-spacing: .05em; }
.panel-meta { font: 10px Inter, Arial, sans-serif; letter-spacing: .08em; color: #a2adb7; }
.signal-node small { font: 10px Inter, Arial, sans-serif; letter-spacing: .1em; }
.panel-foot { font: 11px Inter, Arial, sans-serif; line-height: 1.6; color: #96a5af; }
.lens-tabs button span:first-child, .principles article > span { font: 11px Inter, Arial, sans-serif; }
.signal-question { font-size: 14px; line-height: 1.6; }
.primary-action { font-weight: 500; padding: 16px 24px; letter-spacing: .015em; }
.quiet-link { font-size: 13px; }
.principle-row { font-size: 13px; }
@media(max-width:960px) { h1 { font-size: 40px; } .hero-intro { font-size: 18px; } }
@media(max-width:650px) {
    h1 { font-size: clamp(35px, 10.3vw, 46px); letter-spacing: -.035em; }
    .overline { font-size: 10px; }
    .hero-baseline { font-size: 10px; line-height: 1.6; }
    .panel-meta { font-size: 9px; gap: 15px; line-height: 1.6; }
    .principle-row { font-size: 11px; gap: 8px; line-height: 1.6; }
}
</style>
